import React from 'react'
import { UserRole } from '@myapp/contract'
import { API_URL } from '../../../shared/config/axios'
import { getPositionsFromRoles } from '../lib/getPositionFromRole'

export interface TechnicianTradeChip {
  id: number
  label: string
  /** Stands out: it is the Gewerk of the Auftrag being planned. */
  matched?: boolean
}

/** More chips than this collapse into a "+N" chip — the card is 224px wide and two lines tall. */
const MAX_VISIBLE_TRADES = 2

interface Props {
  technicianId: string
  technicianName: string | null
  technicianRoles: UserRole[]
  technicianAvatar: string | null
  /** Greyed out, still fully usable — the card's white backing stays opaque, only its content fades. */
  muted?: boolean
  /**
   * The technician's Gewerke. When given they replace the role line, which on a board of Handwerker
   * only ever says "Handwerker".
   */
  trades?: TechnicianTradeChip[]
  /**
   * Controls drawn at the card's right edge, outside the `muted` fade — the card does not know what
   * they do. The disposition board puts the drag handle and the hide toggle of the order editor here.
   */
  actions?: React.ReactNode
}

export const TechnicianInfo = ({ technicianId, technicianName, technicianRoles, technicianAvatar, muted = false, trades, actions }: Props) => {
  const initials = (technicianName ?? technicianId).slice(0, 2).toUpperCase()
  const avatarUrl = technicianAvatar ? `${API_URL}/static/${technicianAvatar}` : null

  return (
    // No `sticky`, no `z-*`: where this cell sits and what it has to paint over belong to the board
    // that lays it out, not to the card. The disposition board wraps it in a sticky positioned box
    // (`BOARD_LAYER.technicianColumn`); an entity cannot import that scale without importing upwards.
    <div className="w-56 h-full shrink-0 self-stretch p-3 bg-white outline outline-1 outline-offset-[-0.50px] outline-slate-200 flex items-center gap-3.5" data-muted={muted || undefined}>
      <div className={`flex min-w-0 flex-1 items-center gap-3.5 ${muted ? "opacity-50 grayscale" : ""}`}>
      {avatarUrl ? (
        <img
          src={avatarUrl}
          alt={technicianName ?? technicianId}
          className="w-11 h-11 rounded-full object-cover shrink-0"
        />
      ) : (
        <div className="w-11 h-11 rounded-full bg-blue-900 flex items-center justify-center text-white text-sm font-medium shrink-0">
          {initials}
        </div>
      )}
      <div className="flex-1 min-w-0 flex flex-col gap-0.5">
        <div className="text-slate-900 text-sm font-normal font-['Roboto'] leading-4 truncate">
          {technicianName ?? technicianId}
        </div>
        {trades ? (
          <TradeChips trades={trades} />
        ) : (
          <div className="text-zinc-500 text-xs font-normal font-['Roboto'] leading-4 min-h-8">
            {getPositionsFromRoles(technicianRoles)}
          </div>
        )}
      </div>
      </div>
      {actions}
    </div>
  )
}

const TradeChips = ({ trades }: { trades: TechnicianTradeChip[] }) => {
  const visible = trades.slice(0, MAX_VISIBLE_TRADES)
  const hidden = trades.slice(MAX_VISIBLE_TRADES)

  return (
    <ul className="flex min-h-8 flex-wrap content-start gap-1" aria-label="Gewerke">
      {visible.map((trade) => (
        <li
          key={trade.id}
          title={trade.label}
          className={`max-w-full truncate rounded px-1.5 text-[11px] font-['Roboto'] leading-4 ${
            trade.matched
              ? 'bg-[#124093] font-medium text-white'
              : 'bg-slate-100 font-normal text-slate-600'
          }`}
        >
          {trade.label}
        </li>
      ))}
      {hidden.length > 0 && (
        <li
          title={hidden.map((trade) => trade.label).join(', ')}
          className="rounded bg-slate-100 px-1.5 text-[11px] font-normal font-['Roboto'] leading-4 text-slate-600"
        >
          +{hidden.length}
        </li>
      )}
    </ul>
  )
}
